import { EmptyState } from '@crowdex/ui/components/feedback';
import { Quote, type Terminal } from 'lucide-react';
import { useEffect, useEffectEvent, useRef, useState } from 'react';
import { useActiveAppearance } from '@/lib/appearance/active-appearance';
import { useTerminalPalette } from '@/lib/appearance/appearance-provider';
import { TERMINAL_THEME } from '@/lib/appearance/terminal-palette';
import type { TerminalSession } from '@/lib/desktop-panel-helpers';
import { errorMessage } from '@/lib/error-message';
import type { WorkspaceClient } from '@/lib/workspaces/workspace-client';

/** The theme's monospace stack: xterm measures a concrete font list, not a CSS variable. */
function monospaceFont(element: Element): string {
  return (
    getComputedStyle(element).getPropertyValue('--font-mono-value').replace(/\s+/g, ' ').trim() ||
    'monospace'
  );
}

export type { TerminalSession } from '@/lib/desktop-panel-helpers';

// A terminal's initial command must run exactly once even though React can
// mount the pane twice (StrictMode in dev) or re-mount it on tab toggles.
// Tracked by terminal id across mounts and cleared when the pty is torn down.
const startedTerminalCommands = new Set<string>();

function TerminalSessionPane({
  active,
  onAskSelectionInSideChat,
  onExit,
  onReferenceSelection,
  onMoreDetailsSelection,
  onTitleChange,
  session,
  workspace,
  client,
}: {
  active: boolean;
  onAskSelectionInSideChat: (content: string) => void;
  onExit: (code: number | null) => void;
  onReferenceSelection: (content: string, anchor: { x: number; y: number }) => void;
  onMoreDetailsSelection: (content: string) => void;
  onTitleChange: (title: string) => void;
  session: TerminalSession;
  workspace: string;
  client: WorkspaceClient;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [selection, setSelection] = useState('');
  const onExitRef = useRef(onExit);
  onExitRef.current = onExit;
  const killTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const palette = useTerminalPalette() ?? TERMINAL_THEME;
  const paletteRef = useRef(palette);
  paletteRef.current = palette;
  const terminalRef = useRef<{
    terminal: { options: { theme?: unknown; fontFamily?: string } };
    fit: () => void;
  } | null>(null);
  const { appearance, variant } = useActiveAppearance();
  const codeFont = appearance[variant].chrome.fonts.code;
  useEffect(() => {
    if (terminalRef.current) terminalRef.current.terminal.options.theme = palette;
  }, [palette]);
  // A new code font reaches open terminals too, measured again for the new glyph size.
  useEffect(() => {
    const current = terminalRef.current;
    const container = containerRef.current;
    if (!current || !container || codeFont === undefined) return;
    const font = monospaceFont(container);
    if (current.terminal.options.fontFamily === font) return;
    current.terminal.options.fontFamily = font;
    current.fit();
  }, [codeFont]);

  // The latest callback, without spawning the terminal again when it changes.
  const titleChanged = useEffectEvent((title: string) => onTitleChange(title));
  // biome-ignore lint/correctness/useExhaustiveDependencies(workspace): the terminal is spawned again in another workspace, which the client may serve too.
  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    const terminalId = session.id;
    // Cancel a pending teardown kill: a fast re-mount (StrictMode) should reuse
    // the live pty instead of killing and re-spawning it.
    if (killTimerRef.current !== null) {
      clearTimeout(killTimerRef.current);
      killTimerRef.current = null;
    }
    let disposed = false;
    let teardown = () => undefined;

    void (async () => {
      const [{ Terminal: XTerm }, { FitAddon }] = await Promise.all([
        import('@xterm/xterm'),
        import('@xterm/addon-fit'),
      ]);
      if (disposed) return;
      const terminal = new XTerm({
        allowProposedApi: false,
        convertEol: false,
        cursorBlink: true,
        cursorStyle: 'block',
        fontFamily: monospaceFont(container),
        fontSize: 13,
        lineHeight: 1.25,
        scrollback: 10_000,
        theme: paletteRef.current,
      });
      const fitAddon = new FitAddon();
      terminal.loadAddon(fitAddon);
      terminalRef.current = {
        terminal,
        fit: () => {
          try {
            fitAddon.fit();
          } catch {
            // The panel can be hidden while another tab is active.
          }
        },
      };
      terminal.open(container);
      fitAddon.fit();

      const showExit = (code: number | null) => {
        onExitRef.current(code);
        terminal.write(`\r\n\x1b[90m[process exited ${code ?? ''}]\x1b[0m\r\n`);
      };
      const input = terminal.onData((data) => client.writeTerminal(terminalId, data));
      const titleChange = terminal.onTitleChange((title) => titleChanged(title));
      const selectionChange = terminal.onSelectionChange(() =>
        setSelection(terminal.getSelection()),
      );
      const resize = terminal.onResize(({ cols, rows }) =>
        client.resizeTerminal(terminalId, cols, rows),
      );
      const observer = new ResizeObserver(() => {
        try {
          fitAddon.fit();
        } catch {
          // The panel can be hidden while another tab is active.
        }
      });
      observer.observe(container);

      try {
        await client.spawnTerminal(
          terminalId,
          terminal.cols,
          terminal.rows,
          { onOutput: (data) => terminal.write(data), onExit: showExit },
          session.env,
        );
        if (session.initialCommand !== undefined && !startedTerminalCommands.has(terminalId)) {
          startedTerminalCommands.add(terminalId);
          client.writeTerminal(terminalId, `${session.initialCommand}\n`);
        }
        terminal.focus();
      } catch (error) {
        terminal.writeln(`\x1b[31m${errorMessage(error)}\x1b[0m`);
      }

      teardown = () => {
        observer.disconnect();
        input.dispose();
        titleChange.dispose();
        selectionChange.dispose();
        resize.dispose();
        terminalRef.current = null;
        terminal.dispose();
        // Defer the kill so a StrictMode/tab re-mount can cancel it and reuse
        // the pty. A real unmount lets the timer fire and clean up (which also
        // unsubscribes the terminal's output/exit listeners).
        killTimerRef.current = setTimeout(() => {
          killTimerRef.current = null;
          startedTerminalCommands.delete(terminalId);
          client.killTerminal(terminalId);
        }, 250);
      };
      if (disposed) teardown();
    })();

    return () => {
      disposed = true;
      teardown();
    };
  }, [client, session.env, session.id, session.initialCommand, workspace]);

  return (
    <div className={`relative h-full min-h-72 ${active ? '' : 'hidden'}`}>
      {selection.trim().length > 0 && (
        <div
          aria-label="Terminal selection actions"
          className="absolute top-2 right-4 z-10 flex overflow-hidden rounded-lg border border-white/15 bg-slate-900/95 text-slate-200 text-xs shadow-lg"
          onMouseDown={(event) => {
            event.preventDefault();
          }}
          role="toolbar"
        >
          <button
            className="border-white/15 border-r px-3 py-2 hover:bg-slate-800"
            onClick={(event) => {
              const bounds = event.currentTarget.getBoundingClientRect();
              onReferenceSelection(selection, { x: bounds.left, y: bounds.bottom + 8 });
            }}
            type="button"
          >
            Add to chat
          </button>
          <button
            className="flex items-center gap-1.5 border-white/15 border-r px-3 py-2 hover:bg-slate-800"
            onClick={() => onMoreDetailsSelection(selection)}
            type="button"
          >
            <Quote className="size-3.5" />
            More details
          </button>
          <button
            className="px-3 py-2 hover:bg-slate-800"
            onClick={() => onAskSelectionInSideChat(selection)}
            type="button"
          >
            Ask in side chat
          </button>
        </div>
      )}
      <div
        aria-label="Interactive terminal"
        className="crowdex-terminal h-full min-h-72 w-full overflow-hidden bg-desktop-terminal p-3"
        ref={containerRef}
        role="application"
      />
    </div>
  );
}

export type AgentTerminalDisplay = {
  id: string;
  command: string;
  output: string;
  truncated: boolean;
  exitCode: number | null;
  signal: string | null;
  running: boolean;
  outputFile?: string;
  backgroundCompleted?: boolean;
  toolState?: import('@/lib/conversations/message-part/types').DynamicToolPart['state'];
  /** The folder the command ran in, when its agent reports it. */
  cwd?: string;
  /** How long the command ran, once it ended. */
  durationMs?: number;
};

export function TerminalPane({
  client,
  onAskSelectionInSideChat,
  onMoreDetailsSelection,
  onReferenceSelection,
  onTitleChange,
  session,
  workspace,
}: {
  client: WorkspaceClient;
  onAskSelectionInSideChat: (content: string) => void;
  onMoreDetailsSelection: (content: string) => void;
  onReferenceSelection: (content: string, anchor: { x: number; y: number }) => void;
  onTitleChange: (title: string) => void;
  session: TerminalSession;
  workspace: string;
}) {
  return (
    <div className="h-full min-h-72 bg-desktop-terminal">
      <TerminalSessionPane
        active
        client={client}
        onAskSelectionInSideChat={onAskSelectionInSideChat}
        onMoreDetailsSelection={onMoreDetailsSelection}
        onExit={() => undefined}
        onReferenceSelection={onReferenceSelection}
        onTitleChange={onTitleChange}
        session={session}
        workspace={workspace}
      />
    </div>
  );
}

export function PanelEmpty({
  icon: Icon,
  text,
  title,
}: {
  icon: typeof Terminal;
  text: string;
  title: string;
}) {
  return (
    <EmptyState
      className="min-h-72 justify-center"
      icon={<Icon />}
      title={title}
      description={text}
    />
  );
}
